import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { toast } from 'sonner';
import {
  Camera, Download, Star, Printer, Heart, Share2,
  QrCode, ArrowLeft, Check, ShoppingBag, X, Plus, Minus,
  ChevronRight, Lock, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PhotoboothEventPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [galerie, setGalerie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});   // { photo_id: quantite }
  const [showQR, setShowQR] = useState(false);
  const [showReprint, setShowReprint] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [email, setEmail] = useState('');
  const [lightbox, setLightbox] = useState(null);

  const paiementOk = searchParams.get('paiement') === 'succes';
  const reimpOk    = searchParams.get('reimp') === 'succes';

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/photobooth/galerie/${token}`);
      if (!res.ok) throw new Error();
      setGalerie(await res.json());
    } catch { /* 404 handled below */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function toggleFavori(photoId) {
    await fetch(`${BACKEND}/api/photobooth/galerie/${token}/favori/${photoId}`, { method: 'POST' });
    load();
  }

  function toggleSelect(photoId) {
    setSelected(prev => {
      const next = { ...prev };
      if (next[photoId]) delete next[photoId];
      else next[photoId] = 1;
      return next;
    });
  }

  function changeQty(photoId, delta) {
    setSelected(prev => {
      const qty = Math.max(0, (prev[photoId] || 0) + delta);
      const next = { ...prev };
      if (qty === 0) delete next[photoId];
      else next[photoId] = qty;
      return next;
    });
  }

  const selectedList = Object.entries(selected);
  const totalQty     = selectedList.reduce((s, [, q]) => s + q, 0);
  const totalPrix    = (totalQty * 0.70).toFixed(2);

  async function handleReprint() {
    if (totalQty === 0) { toast.error('Sélectionnez des photos et quantités'); return; }
    if (!email) { toast.error('Votre email est requis'); return; }
    setCheckingOut(true);
    try {
      const photos = selectedList.map(([id, quantite]) => ({ photo_id: id, quantite }));
      const res = await fetch(`${BACKEND}/api/photobooth/galerie/${token}/reimprimer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photos, email, origin_url: window.location.origin })
      });
      const d = await res.json();
      if (d.url) window.location.href = d.url;
      else toast.error(d.detail || 'Erreur paiement');
    } catch { toast.error('Erreur'); }
    setCheckingOut(false);
  }

  if (loading) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div className="animate-spin w-10 h-10 rounded-full" style={{ border: '2px solid var(--border)', borderTopColor: 'var(--teal)' }} />
      </div>
    </div>
  );

  if (!galerie) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <div className="container-ose text-center" style={{ paddingTop: '8rem' }}>
        <Lock size={48} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
        <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--ink)' }}>Galerie introuvable</h2>
        <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Ce lien est invalide ou la galerie n'est pas encore disponible.</p>
        <Link to="/" className="btn-teal px-6 py-3 rounded-xl font-semibold inline-flex">Retour à l'accueil</Link>
      </div>
      <Footer />
    </div>
  );

  const photos = galerie.photos || [];

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '6rem' }}>

        {/* Notifications succès */}
        {paiementOk && (
          <div className="flex items-center gap-3 p-4 rounded-2xl mb-6" style={{ background: 'rgba(31,157,122,0.06)', border: '1px solid rgba(31,157,122,0.2)' }}>
            <Check size={20} style={{ color: '#1F9D7A' }} />
            <p className="text-sm font-semibold" style={{ color: '#1F9D7A' }}>Paiement confirmé ! Votre événement est totalement soldé.</p>
          </div>
        )}
        {reimpOk && (
          <div className="flex items-center gap-3 p-4 rounded-2xl mb-6" style={{ background: 'rgba(31,157,122,0.06)', border: '1px solid rgba(31,157,122,0.2)' }}>
            <Printer size={20} style={{ color: '#1F9D7A' }} />
            <p className="text-sm font-semibold" style={{ color: '#1F9D7A' }}>Réimpression commandée ! Nous vous contacterons pour la livraison.</p>
          </div>
        )}

        {/* Header galerie */}
        <div className="flex items-start gap-5 mb-8 flex-wrap">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--teal-mist)' }}>
            <Camera size={28} style={{ color: 'var(--teal)' }} />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
              {galerie.nom_evenement}
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {galerie.type_photobooth}
              {galerie.date_evenement && ` · ${formatDate(galerie.date_evenement + 'T00:00:00')}`}
              {photos.length > 0 && ` · ${photos.length} photos`}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowQR(q => !q)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all"
              style={{ background: 'rgba(93,188,191,0.1)', color: 'var(--teal)', border: '1px solid rgba(93,188,191,0.2)' }}>
              <QrCode size={14} /> QR Code
            </button>
            {selectedList.length > 0 && (
              <button onClick={() => setShowReprint(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all"
                style={{ background: 'var(--teal)', color: '#fff' }}>
                <Printer size={14} /> Réimprimer ({totalQty})
              </button>
            )}
          </div>
        </div>

        {/* QR Code */}
        {showQR && galerie.qr_code_url && (
          <div className="flex items-center gap-5 p-5 rounded-2xl mb-6" style={{ background: '#fff', border: '1px solid var(--border)' }}>
            <img src={galerie.qr_code_url} alt="QR Code galerie" className="w-32 h-32 rounded-xl" />
            <div>
              <p className="font-semibold mb-1" style={{ color: 'var(--ink)' }}>Partagez cette galerie</p>
              <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>Scannez le QR code pour accéder aux photos depuis n'importe quel appareil.</p>
              <button onClick={() => navigator.clipboard?.writeText(window.location.href).then(() => toast.success('Lien copié !'))}
                className="flex items-center gap-2 text-xs font-semibold"
                style={{ color: 'var(--teal)' }}>
                <Share2 size={12} /> Copier le lien
              </button>
            </div>
          </div>
        )}

        {/* Message tarifaire */}
        {galerie.nb_impressions > 0 && (
          <div className="p-4 rounded-2xl mb-6 flex items-center gap-3" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
            <Zap size={18} style={{ color: '#D4AF37', flexShrink: 0 }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                {galerie.nb_impressions} impressions utilisées
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Vous ne payez que ce que vous avez consommé. Aucune impression perdue.</p>
            </div>
          </div>
        )}

        {/* Grille photos */}
        {photos.length === 0 ? (
          <div className="text-center py-20">
            <Camera size={56} className="mx-auto mb-4" style={{ color: 'var(--border)', opacity: 0.4 }} />
            <p className="text-lg font-semibold mb-1" style={{ color: 'var(--ink)' }}>Galerie en cours de constitution</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Les photos seront disponibles après votre événement.</p>
          </div>
        ) : (
          <>
            {/* Instruction réimpression */}
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
              <span style={{ color: 'var(--teal)', fontWeight: 600 }}>Cliquez sur une photo</span> pour la sélectionner et demander une réimpression · <span style={{ color: '#D4AF37', fontWeight: 600 }}>0.70€ / impression</span>
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map(photo => {
                const qty   = selected[photo.id] || 0;
                const isSel = qty > 0;

                return (
                  <div key={photo.id} className="relative rounded-2xl overflow-hidden group cursor-pointer"
                    style={{ border: isSel ? '2px solid var(--teal)' : '2px solid transparent', transition: 'border-color 0.15s' }}>
                    {/* Image */}
                    <div className="aspect-square bg-gray-100 relative" onClick={() => setLightbox(photo)}>
                      {photo.url ? (
                        <img src={photo.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--teal-mist)' }}>
                          <Camera size={32} style={{ color: 'var(--teal)', opacity: 0.4 }} />
                        </div>
                      )}
                    </div>

                    {/* Actions overlay */}
                    <div className="absolute top-2 left-2 flex gap-1">
                      <button onClick={() => toggleFavori(photo.id)}
                        className="w-8 h-8 rounded-xl flex items-center justify-center"
                        style={{ background: photo.favori ? 'rgba(229,62,62,0.9)' : 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                        <Heart size={13} style={{ color: '#fff', fill: photo.favori ? '#fff' : 'none' }} />
                      </button>
                    </div>

                    {/* Sélection réimpression */}
                    <div className="absolute bottom-0 left-0 right-0 p-2" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
                      {isSel ? (
                        <div className="flex items-center justify-between">
                          <button onClick={() => changeQty(photo.id, -1)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.1)' }}>
                            <Minus size={12} style={{ color: '#fff' }} />
                          </button>
                          <span className="text-sm font-bold" style={{ color: '#fff' }}>{qty} ×</span>
                          <button onClick={() => changeQty(photo.id, 1)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(93,188,191,0.5)' }}>
                            <Plus size={12} style={{ color: '#fff' }} />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => toggleSelect(photo.id)}
                          className="w-full text-xs font-semibold py-1 rounded-lg"
                          style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                          <Printer size={10} className="inline mr-1" /> Réimprimer
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Bouton télécharger toutes */}
        {photos.length > 0 && (
          <div className="mt-8 text-center">
            <Button style={{ background: 'var(--teal)', color: '#fff' }}
              onClick={() => toast.info('Téléchargement en cours... (fonctionnalité liée à l\'hébergement photo)')}
            >
              <Download size={16} className="mr-2" /> Télécharger toutes les photos
            </Button>
          </div>
        )}
      </div>

      {/* MODAL RÉIMPRESSION */}
      {showReprint && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.5)' }}
          onClick={e => { if (e.target === e.currentTarget) setShowReprint(false); }}>
          <div className="w-full max-w-md rounded-2xl p-5 space-y-4" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-lg" style={{ color: 'var(--ink)' }}>Demander des réimpressions</h2>
              <button onClick={() => setShowReprint(false)} style={{ color: 'var(--text-muted)' }}><X size={18} /></button>
            </div>

            {/* Récap */}
            <div className="p-3 rounded-xl" style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.2)' }}>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--ink)' }}>Récapitulatif</p>
              {selectedList.map(([id, qty]) => {
                const photo = photos.find(p => p.id === id);
                return (
                  <div key={id} className="flex items-center justify-between text-sm py-1">
                    <div className="flex items-center gap-2">
                      {photo?.url && <img src={photo.url} alt="" className="w-10 h-10 rounded object-cover" />}
                      <span style={{ color: 'var(--ink)' }}>{qty} impression{qty > 1 ? 's' : ''}</span>
                    </div>
                    <span className="font-semibold price-gold">{(qty * 0.70).toFixed(2)}€</span>
                  </div>
                );
              })}
              <div className="flex justify-between font-bold pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--ink)' }}>Total</span>
                <span className="price-gold">{totalPrix}€ TTC</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>0.70€ TTC / impression (tarif tranche 1)</p>
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>Votre email (pour confirmation)</label>
              <Input type="email" placeholder="votre@email.fr" value={email}
                onChange={e => setEmail(e.target.value)} />
            </div>

            <Button onClick={handleReprint} disabled={checkingOut} className="w-full font-bold"
              style={{ background: 'var(--teal)', color: '#fff' }}>
              {checkingOut ? 'Redirection...' : <><ShoppingBag size={16} className="mr-2" /> Payer {totalPrix}€ — Stripe sécurisé</>}
            </Button>
            <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>Paiement 100% sécurisé</p>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.9)' }}
          onClick={() => setLightbox(null)}>
          <img src={lightbox.url} alt="" className="max-w-full max-h-full rounded-xl"
            style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            onClick={e => e.stopPropagation()} />
          <button onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}>
            <X size={18} />
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}
